import React from 'react';
import { useEffect, useState } from 'react';
import SelectCardCondition from '../common/selects/SelectCardCondition';
import SelectCardLanguage from '../common/selects/SelectCardLanguage';
import SelectCardPriceRank from '../common/selects/SelectCardPriceRank';
import SelectStore from '../common/selects/SelectStore';
import Button from '../common/Button';
import Input from '../common/Input';
import { manaSymbolUrl } from '../../utils/manaSymbols';

/**
 * Every filter this component can render. A section passes the subset it wants
 * through the `fields` prop; the default is all of them.
 *
 * `colors` and `cmc` used to live in a separate AdvancedCardFilters component
 * nested inside this one via children, which meant two `loadData` callbacks and
 * a remount-by-key hack to reset the inner one.
 */
export const CARD_FILTER_FIELDS = [
  'condition',
  'priceRank',
  'language',
  'price',
  'types',
  'stores',
  'colors',
  'cmc',
];

const COLORS = [
  { key: 'colorW', letter: 'W', label: 'Blanco' },
  { key: 'colorU', letter: 'U', label: 'Azul' },
  { key: 'colorB', letter: 'B', label: 'Negro' },
  { key: 'colorR', letter: 'R', label: 'Rojo' },
  { key: 'colorG', letter: 'G', label: 'Verde' },
];

const SELECT_CLASS =
  'block w-full h-10 px-3 py-1.5 text-base font-normal leading-6 text-gray-600 bg-white border border-gray-300 rounded-md';

const INITIAL_STATE = {
  condition: null,
  language: null,
  priceRank: null,
  minPrice: '',
  maxPrice: '',
  types: '',
  stores: null,
  colorW: false,
  colorU: false,
  colorB: false,
  colorR: false,
  colorG: false,
  colorless: false,
  colorMode: 'at_least',
  cmc: '',
  cmcComparator: 'eq',
};

const CardFilters = ({ loadData, onReset, fields = CARD_FILTER_FIELDS }) => {
  const [state, setState] = useState(INITIAL_STATE);

  const shows = field => fields.indexOf(field) !== -1;

  useEffect(() => {
    loadData(getFiltersObject());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // One shape for both halves: `where` goes to the card query as-is, `advanced`
  // holds the Scryfall-style filters the sections apply themselves. Hidden fields
  // stay at their initial value, so they emit as null and filter nothing.
  const getFiltersObject = () => {
    const selectedColorLetters = COLORS.filter(color => state[color.key]).map(
      color => color.letter,
    );

    return {
      where: {
        conditionRank: state.condition,
        language: state.language,
        priceRank: state.priceRank,
        minPrice: state.minPrice || null,
        maxPrice: state.maxPrice || null,
        types: state.types || null,
        stores: state.stores || null,
      },
      advanced: {
        colors: state.colorless
          ? ['C']
          : selectedColorLetters.length
          ? selectedColorLetters
          : null,
        colorMode: state.colorless ? null : state.colorMode,
        cmc: state.cmc !== '' ? Number(state.cmc) : null,
        cmcComparator: state.cmc !== '' ? state.cmcComparator : null,
      },
    };
  };

  const onFiltersChange = event => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    setState(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const toggleColor = key => {
    setState(prevState => ({ ...prevState, [key]: !prevState[key] }));
  };

  // Colorless is exclusive: it clears and disables the five colors.
  const toggleColorless = () => {
    setState(prevState => ({
      ...prevState,
      colorless: !prevState.colorless,
      colorW: false,
      colorU: false,
      colorB: false,
      colorR: false,
      colorG: false,
    }));
  };

  const onFiltersRefresh = () => {
    // Resetting here covers the colors and mana value too, so sections no longer
    // need to remount anything to clear them.
    setState(INITIAL_STATE);
    onReset && onReset();
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3 flex-wrap items-center">
      {shows('condition') && (
        <div className="w-full sm:w-[15%]">
          <SelectCardCondition
            value={state.condition}
            name={'condition'}
            placeholder={'Cond. mínima'}
            onChange={onFiltersChange}
          />
        </div>
      )}
      {shows('priceRank') && (
        <div className="w-full sm:w-[15%]">
          <SelectCardPriceRank
            value={state.priceRank}
            name={'priceRank'}
            placeholder={'Rango de precio'}
            onChange={onFiltersChange}
          />
        </div>
      )}
      {shows('language') && (
        <div className="w-full sm:w-[15%]">
          <SelectCardLanguage
            value={state.language}
            name={'language'}
            placeholder={'Idioma'}
            onChange={onFiltersChange}
          />
        </div>
      )}
      {shows('price') && (
        <>
          <div className="w-full sm:w-[15%]">
            <Input
              type="number"
              name="minPrice"
              placeholder="Precio Mín USD"
              value={state.minPrice}
              onChange={onFiltersChange}
            />
          </div>
          <div className="w-full sm:w-[15%]">
            <Input
              type="number"
              name="maxPrice"
              placeholder="Precio Máx USD"
              value={state.maxPrice}
              onChange={onFiltersChange}
            />
          </div>
        </>
      )}
      {shows('types') && (
        <div className="w-full sm:w-[20%]">
          <Input
            type="text"
            name="types"
            placeholder="Tipo (ej: Elf, Artifact)"
            value={state.types}
            onChange={onFiltersChange}
          />
        </div>
      )}
      {shows('stores') && (
        <div className="w-full sm:w-[15%]">
          <SelectStore
            value={state.stores}
            name={'stores'}
            placeholder={'Tienda'}
            onChange={onFiltersChange}
          />
        </div>
      )}
      {shows('colors') && (
        <>
          <div className="w-full sm:w-auto flex flex-row flex-wrap items-center gap-1">
            {COLORS.map(color => (
              <button
                key={color.key}
                type="button"
                title={color.label}
                disabled={state.colorless}
                onClick={() => toggleColor(color.key)}
                className={`w-9 h-9 rounded-full border-2 transition-opacity ${
                  state[color.key]
                    ? 'border-accentDark opacity-100'
                    : 'border-transparent opacity-40'
                } ${
                  state.colorless ? 'cursor-not-allowed' : 'hover:opacity-100'
                }`}>
                <img src={manaSymbolUrl(color.letter)} alt={color.label} />
              </button>
            ))}
            <button
              type="button"
              title="Incoloro"
              onClick={toggleColorless}
              className={`w-9 h-9 rounded-full border-2 transition-opacity hover:opacity-100 ${
                state.colorless
                  ? 'border-accentDark opacity-100'
                  : 'border-transparent opacity-40'
              }`}>
              <img src={manaSymbolUrl('C')} alt="Incoloro" />
            </button>
          </div>
          <div className="w-full sm:w-56">
            <select
              name="colorMode"
              className={SELECT_CLASS}
              value={state.colorMode}
              onChange={onFiltersChange}
              disabled={state.colorless}>
              <option value="at_least">Al menos estos colores</option>
              <option value="exact">Exactamente estos colores</option>
              <option value="at_most">Como máximo estos colores</option>
            </select>
          </div>
        </>
      )}
      {shows('cmc') && (
        <>
          <div className="w-full sm:w-20">
            <Input
              type="number"
              name="cmc"
              placeholder="CMC"
              value={state.cmc}
              onChange={onFiltersChange}
            />
          </div>
          <div className="w-full sm:w-[15%]">
            <select
              name="cmcComparator"
              className={SELECT_CLASS}
              value={state.cmcComparator}
              onChange={onFiltersChange}
              disabled={state.cmc === ''}>
              <option value="eq">Costo exacto</option>
              <option value="lte">Costo máximo</option>
              <option value="gte">Costo mínimo</option>
            </select>
          </div>
        </>
      )}
      <div className="w-full sm:w-[15%]">
        <Button iconClass="fas fa-sync" onClick={() => onFiltersRefresh()}>
          Reiniciar filtros
        </Button>
      </div>
    </div>
  );
};

export default CardFilters;
